import React, { useCallback } from 'react';
import styled, { FlattenSimpleInterpolation } from 'styled-components';

import { IconClose } from '@/icons';
import { Button } from '@/ui/Button/Button';
import { Flex } from '@/ui/Flex/Flex';
import { T } from '@/ui/Text/Text';

const StyledBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${({ theme }) => theme.zindex.modalBackdrop};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledDialog = styled.div<{
  readonly styles: FlattenSimpleInterpolation;
}>`
  position: relative;
  flex-grow: 1;
  max-width: 680px;
  min-width: 460px;
  background: ${({ theme }) => theme.color('#FFFFFF')};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  z-index: ${({ theme }) => theme.zindex.modal};

  ${({ styles }) => styles}
`;

const StyledFlexWrapper = styled.div<{
  readonly styles: FlattenSimpleInterpolation;
}>`
  display: flex;
  flex-direction: column;
  padding: 32px;

  ${({ styles }) => styles}
`;

const StyledTitle = styled(Flex)`
  border-bottom: solid 1px ${({ theme }) => theme.color('#ECEAF5')};
`;

const StyledFooter = styled(Flex)`
  border-top: solid 1px ${({ theme }) => theme.color('#ECEAF5')};
`;

type Props = {
  readonly title: React.ReactNode;
  readonly children: React.ReactNode;
  readonly onClose?: () => void;
  readonly footer?: React.ReactNode;
  readonly styles?: FlattenSimpleInterpolation;
  readonly contentStyles?: FlattenSimpleInterpolation;
};

export const Modal = ({
  title,
  children,
  footer,
  onClose,
  styles = {} as FlattenSimpleInterpolation,
  contentStyles = {} as FlattenSimpleInterpolation,
}: Props) => {
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose?.();
      }
    },
    [onClose],
  );

  const handleModalClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click from reaching elements below
  }, []);

  return (
    <StyledBackdrop onClick={handleBackdropClick}>
      <StyledDialog styles={styles} onClick={handleModalClick}>
        <StyledTitle align="center" justify="space-between" padding="24px 32px">
          <T h3>{title}</T>

          {onClose ? (
            <Button
              mode="transparent"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              <IconClose />
            </Button>
          ) : null}
        </StyledTitle>
        <StyledFlexWrapper styles={contentStyles}>{children}</StyledFlexWrapper>
        {footer ? <StyledFooter>{footer}</StyledFooter> : null}
      </StyledDialog>
    </StyledBackdrop>
  );
};
